'use client'

import { useEffect, useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { FeedbackFormModal } from '@marketplace-web/shared/ui-helpers'

import {
  clickEvent,
  viewScreenEvent,
  getOfflineVerificationFeedback,
  sendOfflineVerificationFeedback,
  transformFeedbackRatingsResponse,
  UpdateFeedbackRatingOptionsArgs,
} from '@marketplace-web/conversation-feedback/conversation-feedback-data'

import { isStringTruthy, removeFeedbackUrlParam } from './utils'

const OfflineVerificationFeedback = () => {
  const { relativeUrl, searchParams, replaceHistoryState, urlQuery } = useBrowserNavigation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { transaction } = useConversationContext()
  const { track } = useTracking()

  const { offline_verification_feedback_seller } = searchParams
  const showFeedback = isStringTruthy(offline_verification_feedback_seller)
  const urlWithoutFeedback = removeFeedbackUrlParam(relativeUrl, urlQuery)

  const { transformedData: feedbackRatings, fetch: fetchOfflineVerificationFeedback } = useFetch(
    getOfflineVerificationFeedback,
    transformFeedbackRatingsResponse,
  )

  useEffect(() => {
    if (!showFeedback) return

    replaceHistoryState(urlWithoutFeedback)
  }, [showFeedback, replaceHistoryState, urlWithoutFeedback])

  useEffect(() => {
    if (!feedbackRatings) return

    setIsModalOpen(true)
    track(viewScreenEvent({ screen: 'offline_verification_feedback_form' }))
  }, [setIsModalOpen, feedbackRatings, track])

  useEffect(() => {
    if (!showFeedback || !transaction?.id) return

    fetchOfflineVerificationFeedback(transaction.id)
  }, [showFeedback, transaction, fetchOfflineVerificationFeedback])

  const handleModalClose = () => {
    setIsModalOpen(false)

    track(
      clickEvent({
        screen: 'offline_verification_feedback_form',
        target: 'close_screen',
      }),
    )
  }

  const handleFeedbackUpdate = async ({
    rating,
    message,
    feedbackStatementIds,
    finalized,
  }: UpdateFeedbackRatingOptionsArgs) => {
    if (!transaction?.id) return
    if (!finalized) {
      await sendOfflineVerificationFeedback({ transactionId: transaction.id, rating })

      return
    }

    setIsModalOpen(false)

    await sendOfflineVerificationFeedback({
      transactionId: transaction.id,
      rating,
      comment: message,
      feedbackStatementIds,
      finalized,
    })
  }

  if (!isModalOpen) return null

  return (
    <FeedbackFormModal
      isOpen
      ratings={feedbackRatings}
      onClose={handleModalClose}
      onSubmit={handleFeedbackUpdate}
      isCloseActionVisible={false}
      isInputWithoutRatingOptions
    />
  )
}

export default OfflineVerificationFeedback
